import type { LegacyReadStatusId } from '~/client/src/shared/lists/notifications/notificationSchema'
import {
  LEGACY_READ_STATUS,
  NOTIFICATION_FIELD,
} from '~/client/src/shared/lists/notifications/notificationSchema'

export const NOTIFICATION_FILTER_FIELDS = [
  {
    key: NOTIFICATION_FIELD.readStatus,
    prefix: 'readstatus:',
    application: 'readStatus',
  },
  {
    key: NOTIFICATION_FIELD.notificationType,
    prefix: 'ntype:',
    application: 'option',
  },
  {
    key: NOTIFICATION_FIELD.workflowType,
    prefix: 'wftype:',
    application: 'option',
  },
  {
    key: NOTIFICATION_FIELD.workflowStatus,
    prefix: 'status:',
    application: 'option',
  },
  {
    key: NOTIFICATION_FIELD.company,
    prefix: 'company:',
    application: 'dimension',
  },
  {
    key: NOTIFICATION_FIELD.location,
    prefix: 'loc:',
    application: 'dimension',
  },
  {
    key: NOTIFICATION_FIELD.equipment,
    prefix: 'equip:',
    application: 'dimension',
  },
  {
    key: NOTIFICATION_FIELD.editor,
    prefix: 'editor:',
    application: 'dimension',
  },
] as const

export type NotificationFilterField =
  (typeof NOTIFICATION_FILTER_FIELDS)[number]
export type NotificationFilterFieldKey = NotificationFilterField['key']

export type NotificationAppliedFilterSets = {
  [fieldKey: string]: Set<string>
}

export interface ParsedNotificationFilterId {
  readonly key: NotificationFilterFieldKey
  readonly value: string
  readonly prefixedId: string
}

const SAVED_VIEW_FILTER_TYPE_BY_FIELD_KEY: Partial<
  Record<NotificationFilterFieldKey, string>
> = {
  [NOTIFICATION_FIELD.workflowStatus]: 'status',
}

function requireNotificationFilterField(
  key: NotificationFilterFieldKey,
): NotificationFilterField {
  const field = getNotificationFilterField(key)
  if (!field) {
    throw new Error(`Missing notification filter field: ${key}`)
  }
  return field
}

export function getNotificationFilterField(
  key: string,
): NotificationFilterField | undefined {
  return NOTIFICATION_FILTER_FIELDS.find(field => field.key === key)
}

export function prefixedNotificationFilterId(
  key: NotificationFilterFieldKey,
  value: string,
): string {
  const prefix = requireNotificationFilterField(key).prefix
  return value.startsWith(prefix) ? value : `${prefix}${value}`
}

export function parsePrefixedNotificationFilterId(
  id: string,
): ParsedNotificationFilterId | undefined {
  const field = NOTIFICATION_FILTER_FIELDS.find(candidate =>
    id.startsWith(candidate.prefix),
  )
  if (!field) return undefined

  return {
    key: field.key,
    value: id.slice(field.prefix.length),
    prefixedId: id,
  }
}

export function stripKnownNotificationFilterPrefix(
  value: string,
  key: string,
): string {
  const field = getNotificationFilterField(key)
  if (field && value.startsWith(field.prefix)) {
    return value.slice(field.prefix.length)
  }

  const aliasKey = notificationFieldKeyForSavedViewFilterType(key)
  const aliasField =
    aliasKey === undefined ? undefined : getNotificationFilterField(aliasKey)
  if (aliasField && value.startsWith(aliasField.prefix)) {
    return value.slice(aliasField.prefix.length)
  }

  return value
}

export function emptyNotificationAppliedFilters(): NotificationAppliedFilterSets {
  return Object.fromEntries(
    NOTIFICATION_FILTER_FIELDS.map(field => [field.key, new Set<string>()]),
  )
}

export function prefixedNotificationFilterIdsToFilterSets(
  prefixedIds: readonly string[],
): NotificationAppliedFilterSets {
  const filtersToApply = emptyNotificationAppliedFilters()

  for (const id of prefixedIds) {
    const parsed = parsePrefixedNotificationFilterId(id)
    if (parsed) {
      filtersToApply[parsed.key].add(
        prefixedNotificationFilterId(parsed.key, parsed.value),
      )
    }
  }

  return filtersToApply
}

export function toPrefixedNotificationFilterIds(
  appliedFilters: NotificationAppliedFilterSets,
): readonly string[] {
  return Object.values(appliedFilters).flatMap(valueSet => Array.from(valueSet))
}

export function toValuesForNotificationField(
  filtersToApply: NotificationAppliedFilterSets,
  key: string,
): readonly string[] {
  const values = filtersToApply[key]
  if (!values) return []
  return Array.from(values, value =>
    stripKnownNotificationFilterPrefix(value, key),
  )
}

export function isLegacyReadStatusId(id: string): id is LegacyReadStatusId {
  return Object.values(LEGACY_READ_STATUS).includes(id as LegacyReadStatusId)
}

export function notificationSavedViewFilterTypeForFieldKey(
  key: string,
): string {
  return (
    SAVED_VIEW_FILTER_TYPE_BY_FIELD_KEY[key as NotificationFilterFieldKey] ??
    key
  )
}

export function notificationFieldKeyForSavedViewFilterType(
  type: string,
): NotificationFilterFieldKey | undefined {
  const field = NOTIFICATION_FILTER_FIELDS.find(
    candidate =>
      notificationSavedViewFilterTypeForFieldKey(candidate.key) === type,
  )
  return field?.key
}

export function notificationFilterSetsToSavedViewFilterSets(
  appliedFilters: NotificationAppliedFilterSets,
): NotificationAppliedFilterSets {
  return Object.fromEntries(
    Object.entries(appliedFilters).map(([key, valueSet]) => [
      notificationSavedViewFilterTypeForFieldKey(key),
      new Set(valueSet),
    ]),
  )
}

export function notificationSavedViewFilterSetsToFilterSets(
  savedViewFilters: NotificationAppliedFilterSets,
): NotificationAppliedFilterSets {
  const filtersToApply = emptyNotificationAppliedFilters()

  for (const [type, valueSet] of Object.entries(savedViewFilters)) {
    const key = notificationFieldKeyForSavedViewFilterType(type)
    if (!key) continue
    filtersToApply[key] = new Set(valueSet)
  }

  return filtersToApply
}
